import { generateDeviceId } from "~/api/client";
import type { WebAnalyticsEvent } from "~/types/analytics";
import { AnalyticsEventBuilder } from "./analytics";
import { isTrackingGranted } from "./tracking-consent";
import { getWebAnalyticsBuffer } from "./web-analytics-buffer";
import { getSessionId } from "./session";
import { useUserStore } from "./stores/user";
import { isGuestUser, hasCompleteShippingAddress } from "./user";
import { getUserCurrency } from "./utils";

/**
 * First-party web analytics pipeline. Buffers events (batching + page-exit beacon) via its own
 * WebAnalyticsBuffer, pointed at /api/pubsub_events/web. This runs regardless of tracking consent
 * (first-party data → Essential), so it is never gated on c15t.
 */
function webBuffer() {
  // Fetch per call (a cheap singleton lookup) rather than binding once at module load:
  // resetWebAnalyticsBuffer() replaces the instance, and a captured reference would keep pointing at a
  // cleaned-up, dead buffer.
  return getWebAnalyticsBuffer();
}

// device_id and platform are immutable for the page's lifetime, so cache each after first use — no
// reason to repeat their cost (a localStorage read, a userAgent regex) on the click hot path. session_id
// is deliberately NOT cached here: it can roll over on inactivity, so getSessionId() must re-read it
// each call. That read is write-throttled and runs at user-click pace, so its cost is negligible
// (see session.ts).
let cachedDeviceId: string | null = null;
function deviceId(): string {
  if (cachedDeviceId === null) {
    cachedDeviceId = generateDeviceId();
  }
  return cachedDeviceId;
}

let cachedPlatform: string | null = null;
function platform(): string {
  if (cachedPlatform === null) {
    cachedPlatform = AnalyticsEventBuilder.getPlatform();
  }
  return cachedPlatform;
}

/**
 * Whether the GTM container is running right now.
 *
 * `window.dataLayer` cannot answer this. That array is created ahead of the container and exists
 * whether or not one ever arrives, so it reports true even where nothing is listening.
 * `google_tag_manager` is defined by the container itself, which is why it answers the question.
 *
 * "Not running" covers a container that was blocked and one that has not loaded yet, and this cannot
 * tell them apart. Read it on events late in a visit, where only the first is still possible.
 */
function gtmLoaded(): boolean {
  return typeof window !== "undefined" && "google_tag_manager" in window;
}

/**
 * A per-event id, so a backend that retries a delivery can tell a repeat from a new event.
 *
 * `randomUUID` needs a secure context; the fallback is not cryptographically strong and does not need
 * to be, since this only has to be unique across the events of one visit.
 */
function newEventId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Emit one web analytics event. Fills the common envelope (identity, session, route, platform); the
 * caller supplies `event_type` and any event-specific fields. Carries no direct PII — only `user_id`
 * (for joins); country/region/ip are added server-side.
 */
export function trackWebEvent(partial: Pick<WebAnalyticsEvent, "event_type"> & Partial<WebAnalyticsEvent>): void {
  const user = useUserStore.getState().user;

  const event: WebAnalyticsEvent = {
    // Context DEFAULTS the caller may override — `partial` is spread AFTER them so a value the caller
    // passes wins (e.g. order_complete's payment currency, shipping_info_submit's own is_guest) over
    // the generic account-level default. is_guest: a guest still has a user_id, so user_id alone can't
    // distinguish it; membership/currency for segmentation; has_shipping_address for reading the funnel.
    is_guest: isGuestUser(user),
    membership_status: user?.membership?.status ?? null,
    currency: getUserCurrency(user),
    has_shipping_address: hasCompleteShippingAddress(user),
    ...partial,
    // Authoritative envelope — identity / session / route / time. The caller must NOT override these,
    // so they come AFTER the spread. event_time is ms (matches server_time; the algo pipeline uses s).
    event_time: Date.now(),
    user_id: user?._id ?? null,
    device_id: deviceId(),
    session_id: getSessionId(),
    // `route` is the path only (stable → group/aggregate by it); `url` is the full location incl.
    // query + hash (→ use when debugging a specific hit). SPA state often lives in the query.
    route: typeof window !== "undefined" ? window.location.pathname : "",
    url: typeof window !== "undefined" ? window.location.href : "",
    // External acquisition source only: document.referrer does not change on SPA navigation, so it
    // stays constant across a visit. In-app navigation is reconstructed from the ordered route /
    // page_view sequence within a session_id (Phase 3 modeling), not from this field.
    referrer: typeof document !== "undefined" ? document.referrer : "",
    platform: platform(),
    event_id: newEventId(),
    // The Tracking decision travels with the event because the backend cannot see it: c15t keeps the
    // choice in the browser.
    tracking_consent: isTrackingGranted(),
    gtm_loaded: gtmLoaded(),
  };

  webBuffer().addEvent(event);
}
